import React from "react";
import { useTrialUsage } from "@/shared/hooks/useTrialUsage";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";

const LABELS = Object.freeze({
  contacts: "Contacts",
  accounts: "Accounts",
  campaignLeads: "Campaign leads",
});

// What each trial limit covers, shown on hover.
const HINTS = Object.freeze({
  contacts: (limit) =>
    `Your trial includes ${limit} contacts in total: imports, CSV uploads, Prospecting (email only) and contacts added by hand.`,
  accounts: (limit) => `Your trial includes ${limit} accounts. Re-researching an existing account doesn't count.`,
  campaignLeads: (limit) => `Your trial includes ${limit} leads across all your campaigns.`,
});

/**
 * Trial usage chip, e.g. "Contacts 12/50" with a small meter: amber near the limit, red at it.
 * Hover explains the limit. Renders nothing for paid users.
 */
const TrialRemainingCount = ({ resource }) => {
  const usage = useTrialUsage();
  const limit = usage?.limits?.[resource];
  if (limit == null) return null;

  const used = usage.used?.[resource] ?? 0;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const tone = used >= limit ? "error" : pct >= 80 ? "warning" : "secondary";
  const remaining = Math.max(0, limit - used);

  return (
    <CustomCommonTooltip
      content={`${HINTS[resource](limit)} ${remaining > 0 ? `${remaining} left.` : "Limit reached. Upgrade to add more."}`}
    >
      <span className="inline-flex cursor-default items-center gap-2 whitespace-nowrap rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
        <span>{LABELS[resource]}</span>
        <span className="h-1.5 w-12 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700" aria-hidden="true">
          <span
            className={`block h-full rounded-full ${tone === "error" ? "bg-error-500" : tone === "warning" ? "bg-warning-500" : "bg-secondary"}`}
            style={{ width: `${pct}%` }}
          />
        </span>
        <span className={tone === "error" ? "text-error-500" : tone === "warning" ? "text-warning-700 dark:text-warning-500" : ""}>
          {used.toLocaleString()}/{limit.toLocaleString()}
        </span>
      </span>
    </CustomCommonTooltip>
  );
};

export default TrialRemainingCount;
